<template>
  <div>
    <file-pond
      :filePosterMinHeight="44"
      :filePosterMaxHeight="256"
      name="test"
      ref="pond"
      :labelIdle="$t('dropImageHere')"
      :labelFileProcessing="$t('uploading')"
      :labelFileProcessingComplete="$t('uploadComplete')"
      :labelTapToCancel="$t('tapToCancel')"
      :labelFileLoading="$t('loading')"
      :labelFileWaitingForSize="$t('waitingForSize')"
      :labelFileTypeNotAllowed="$t('fileOfInvalidType')"
      :fileValidateTypeLabelExpectedTypes="$t('expectedFileTypes')"
      v-bind:allowRevert="false"
      accepted-file-types="image/jpeg, image/png"
      v-bind:files="files"
      :imageResizeTargetWidth="720"
      :imageTransformOutputQuality="50"
      imageResizeMode="contain"
      imageTransformOutputMimeType="image/jpeg"
    />
    <div class="v-messages mt-2">{{ $t("imageUploadNotice") }}</div>
  </div>
</template>

<script>
import restaurantService from "../services/restaurant.service";

// Import Vue FilePond
import vueFilePond, { setOptions } from "vue-filepond";

// Import FilePond styles
import "filepond/dist/filepond.min.css";

// Import FilePond plugins
// Please note that you need to install these plugins separately
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
import FilePondPluginFilePoster from "filepond-plugin-file-poster";

// Import image preview plugin styles
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";

// Import image preview and file type validation plugins
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

// Create component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview,
  FilePondPluginImageResize,
  FilePondPluginImageTransform,
  FilePondPluginFilePoster
);

export default {
  props: ["type", "dishId", "imageUrl"],
  data() {
    return {
      files: [],
    };
  },
  created: function () {
    setOptions({
      server: {
        process: async (
          fieldName,
          file,
          metadata,
          load,
          error,
          progress,
          abort
        ) => {
          const { data, imageUrl } = await restaurantService.uploadImage(
            localStorage.restaurantId,
            this.type,
            this.dishId,
            file.name
          );

          const formData = new FormData();
          Object.keys(data.fields).forEach((key) =>
            formData.append(key, data.fields[key])
          );
          formData.append("file", file);

          this.$http
            .request({
              method: "POST",
              url: data.url,
              data: formData,
              withCredentials: false,
              headers: {
                "x-auth": null,
              },
              onUploadProgress: (p) => {
                progress(true, p.loaded, p.total);
              },
            })
            .then(() => {
              load(file);
              this.$emit("change-dish-image-url", {
                dishId: this.dishId,
                imageUrl,
              });
            });

          return {
            abort: () => {
              abort();
            },
          };
        },
        load: async (source, load, error, progress, abort, headers) => {
          this.$http
            .request({
              method: "GET",
              url: source.replace(".cdn.", "."),
              withCredentials: false,
              responseType: "blob",
              headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0",
                "x-auth": null,
              },
              onDownloadProgress: (p) => {
                progress(true, p.loaded, p.total);
              },
            })
            .then((response) => {
              load(response.data);
            });
          return {
            abort: () => {
              abort();
            },
          };
        },
        remove: async (source, load, error) => {
          await restaurantService.removeImage(
            localStorage.restaurantId,
            this.type,
            this.dishId,
            source
          );

          this.$emit("change-dish-image-url", {
            dishId: this.dishId,
            imageUrl: null,
          });

          load();
        },
      },
    });
  },
  watch: {
    imageUrl: {
      immediate: true,
      handler(newVal) {
        this.files = newVal
          ? [
              {
                source: newVal,
                options: {
                  type: "local",
                },
              },
            ]
          : [];
      },
    },
  },
  methods: {},
  components: {
    FilePond,
  },
};
</script>

<style>
.filepond--file-poster-wrapper {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  height: 100%;
  margin: 0;
  border-radius: 0.45 em;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.01);
}

.filepond--file-poster {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  pointer-events: none;
  background: #222;
  will-change: transform, opacity;
  border-radius: 0.45em;
}

.filepond--file-poster img {
  display: flex;
  max-height: 100%;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
}

.filepond--file-poster-overlay {
  display: none;
}

.filepond--root {
  margin-bottom: 0;
}

/* the text color of the drop label*/
.filepond--drop-label {
  color: rgba(0, 0, 0, 0.6);
}

/* the background color of the filepond drop area */
.filepond--panel-root {
  background-color: #f0f0f0;
}

/* the background color of the file and file panel (used when dropping an image) */
.filepond--item-panel {
  background-color: #555;
}

/* the background color of the drop circle */
.filepond--drip-blob {
  background-color: #0360ec;
}

/* the background color of the black action buttons */
.filepond--file-action-button {
  background-color: rgba(0, 0, 0, 0.5);
}

/* the color of the focus ring */
.filepond--file-action-button:hover,
.filepond--file-action-button:focus {
  box-shadow: 0 0 0 0.125em rgba(255, 255, 255, 0.9);
}

/* error state color */
[data-filepond-item-state*="error"] .filepond--item-panel,
[data-filepond-item-state*="invalid"] .filepond--item-panel {
  background-color: #fc010e;
}

[data-filepond-item-state="processing-complete"] .filepond--item-panel {
  background-color: #13e46d;
}

.filepond--credits {
  display: none;
}
</style>